import { createFileRoute } from '@tanstack/react-router';
import {
  ActionIcon, Button, Card, Group, Loader, Select, SegmentedControl, Stack, Text, Title,
  type MantineColorScheme,
} from '@mantine/core';
import { IconArrowDown, IconArrowUp, IconX } from '@tabler/icons-react';
import { EmptyState } from '@/shared/components/EmptyState';
import {
  MAX_ENTRIES, toggleEntry, moveEntry, useBarCatalog, useBarEntries,
} from '@/shared/mobile/vociBarra';
import { MaeAdvice } from '@/shared/components/MaeAdvice';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { changeLanguage } from '@/shared/i18n/config';
import type { DetailSurfaceKind } from '@/shared/components/DetailSurface';
import { SURFACE_CHOICES, useSurfacePreference } from '@/shared/detail/surfacePreference';
import { usePageHeaderVisibility } from '@/shared/desktop/pageHeaderVisibility';
import { STORAGE_KEYS } from '@/shared/lib/constants';
import { DARK_THEME_ENABLED } from '@/shared/lib/temaScuro';
import {
  useThemeChoice, savePreferencesUi, QUERY_KEY_UI, PREF_KEY_UI, type PreferencesUi,
} from '@/shared/lib/preferenzaTema';
import { fetchList } from '@/shared/api/jsonapi/client';
import { useCurrentUserId } from '@/shared/hooks/useCurrentUserId';
import { notifications } from '@mantine/notifications';
import type { Denormalized } from '@/shared/api/jsonapi/parser';

import { leggiChiave, writeKey } from '@/shared/lib/storage';
interface PreferencesSearch {
  /** Da quale scheda si arriva: la barra del telefono manda `barra`. */
  section?: string;
}

/*
 * ⚠️ Senza `validateSearch` il parametro **non arriva**: TanStack scarta quello che una rotta non
 * dichiara. Il salto alla scheda giusta sarebbe partito solo se qualcuno avesse aggiunto questa
 * riga — e il difetto e' un collegamento che porta alla pagina giusta e alla schermata sbagliata.
 */
export const Route = createFileRoute('/_app/settings/preferences')({
  component: PreferencesPage,
  validateSearch: (s: Record<string, unknown>): PreferencesSearch => ({
    section: typeof s.sezione === 'string' ? s.sezione : undefined,
  }),
});

const LANGUAGES = [
  { value: 'it-IT', label: 'Italiano' },
  { value: 'en-US', label: 'English' },
];

const TIMEZONES = [
  { value: 'Europe/Rome', label: 'Europe/Rome (CET/CEST)' },
  { value: 'Europe/London', label: 'Europe/London (GMT/BST)' },
  { value: 'UTC', label: 'UTC' },
];

const DATE_FORMATS = [
  { value: 'DD/MM/YYYY', label: 'gg/mm/aaaa (Italia)' },
  { value: 'MM/DD/YYYY', label: 'mm/gg/aaaa (USA)' },
  { value: 'YYYY-MM-DD', label: 'aaaa-mm-gg (ISO)' },
];

const LS_TZ = STORAGE_KEYS.timezone;
const LS_DF = STORAGE_KEYS.dateFormat;
/** Chiave usata dal colorSchemeManager di default di Mantine (nessun manager custom in AppProviders). */

/** prefKey della UserPreference BE: definita in `preferenzaTema.ts`, che e' chi la scrive. */
const PREF_KEY = PREF_KEY_UI;

interface UserPreferenceRow extends Denormalized {
  type: 'user-preferences';
  prefKey?: string;
  prefValue?: string;
}

/** Snapshot serializzato in prefValue (JSON). Definito accanto a chi lo scrive. */
type UiPrefs = PreferencesUi;

function PreferencesPage() {
  const surface = useSurfacePreference();
  const pageHeader = usePageHeaderVisibility();
  const theme = useThemeChoice();
  const [lang, setLang] = useState<string>(leggiChiave(STORAGE_KEYS.lang) ?? 'it-IT');
  const [tz, setTz] = useState<string>(leggiChiave(LS_TZ) ?? 'Europe/Rome');
  const [df, setDf] = useState<string>(leggiChiave(LS_DF) ?? 'DD/MM/YYYY');

  const qc = useQueryClient();
  const userId = useCurrentUserId();

  /* ── le pagine della barra del telefono ── */
  const barCatalog = useBarCatalog();
  const { choices, write } = useBarEntries();
  const nameOf = useCallback(
    (to: string) => barCatalog.find((v) => v.to === to)?.label ?? null,
    [barCatalog],
  );
  /* Quelle non ancora scelte: offrire una voce gia' in barra vorrebbe dire un tocco che la
     toglie da un campo che dice «aggiungi». */
  const toAdd = useMemo(
    () => barCatalog
      .filter((v) => !choices.includes(v.to))
      .map((v) => ({ value: v.to, label: v.label })),
    [barCatalog, choices],
  );

  /*
   * ⚠️ IL SALTO ALLA SCHEDA, e perche' serve: questa e' l'**ultima** della pagina. Chi arriva dal
   * foglio della barra cerca questa, e senza il salto si trova in cima con la cosa per cui e'
   * venuto tre schermate piu' in basso — cioe' un collegamento che porta alla pagina giusta e
   * alla schermata sbagliata. Stesso accorgimento di Lares.
   */
  const { section } = Route.useSearch();
  const barDetail = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (section !== 'barra') return;
    barDetail.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
  }, [section]);

  // Record BE (user-scoped lato server: si filtra solo per prefKey, come useFavoriteProjects).
  const prefQ = useQuery({
    queryKey: ['user-pref', PREF_KEY, userId],
    enabled: Boolean(userId),
    queryFn: async () => {
      const resp = await fetchList<UserPreferenceRow>('user-preferences', {
        filter: { prefKey: { eq: PREF_KEY } },
        page: { size: 1 },
      });
      return resp.data[0] ?? null;
    },
  });

  // Al mount: se esiste il record BE e il valore locale corrispondente è assente,
  // applichiamo il valore BE (localStorage resta autoritativo quando presente).
  const appliedRemote = useRef(false);
  useEffect(() => {
    /* Se la lettura remota fallisce restano valide le preferenze locali: il ramo sotto
       non parte e `prefsFromServerUnavailable` lo segnala nella pagina. */
    if (appliedRemote.current || !prefQ.data?.prefValue) return;
    appliedRemote.current = true;
    let remote: UiPrefs;
    try {
      remote = JSON.parse(prefQ.data.prefValue) as UiPrefs;
    } catch {
      return; // prefValue corrotto: ignoriamo, vince il locale
    }
    /*
     * ⚠️ IL REMOTO VINCE, e prima era il contrario.
     *
     * La condizione era `&& !leggiChiave(...)`: applicare la preferenza del profilo **solo** se il
     * browser non ne aveva già una. Ma il browser una la scrive al primo cambio e non la scade mai,
     * quindi il valore del profilo non arrivava più — su nessun dispositivo già usato. Il server
     * era dichiarato autorità e non lo era mai; e su un PC condiviso l'effetto era peggiore, perché
     * la preferenza «già presente» era quella della persona di prima: si entrava e si trovava la
     * lingua di un collega, con la propria salvata sul profilo e ignorata.
     *
     * Qui il verso giusto è questo: `prefQ` risponde una volta per sessione, e quel valore è
     * l'ultimo che **questa persona** ha scelto, su qualunque dispositivo. Il locale resta la copia
     * che dipinge subito, non quella che decide.
     */
    if (remote.colorScheme) {
      /* La preferenza salvata sul profilo si legge ma non si applica: il provider forza il
         chiaro, e applicarla accenderebbe solo un valore che nessuno vede. Il dato resta a
         database, cosi riaccendendo il tema scuro ciascuno ritrova la sua scelta. */
      if (DARK_THEME_ENABLED) theme.applyWithoutSaving(remote.colorScheme as MantineColorScheme);
    }
    if (remote.lang) {
      writeKey(STORAGE_KEYS.lang, remote.lang);
      setLang(remote.lang);
    }
    if (remote.tz) {
      writeKey(LS_TZ, remote.tz);
      setTz(remote.tz);
    }
    if (remote.dateFormat) {
      writeKey(LS_DF, remote.dateFormat);
      setDf(remote.dateFormat);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefQ.data]);

  useEffect(() => {
    void changeLanguage(lang);
  }, [lang]);

  // Upsert BE: update se il record esiste, create altrimenti. Il fallimento è
  // non bloccante (warning): localStorage resta la fonte autoritativa locale.
  const upsert = useMutation({
    /*
     * ⚠️ La scrittura sta in `preferenzaTema.ts` e non piu' qui, e non e' un riordino: quella
     * **fonde** con il valore che c'e' sul profilo adesso, questa sovrascriveva con lo scatto
     * dello stato locale. Con due comandi che scrivono la stessa riga — questa pagina e il
     * foglio del telefono — sovrascrivere significa che l'ultimo che salva riporta indietro
     * quello che l'altro aveva cambiato: cambiare il fuso qui rimetteva il tema che questa
     * scheda aveva letto all'apertura, cioe' disfaceva una scelta fatta dal telefono.
     */
    mutationFn: (next: UiPrefs) => savePreferencesUi(next),
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEY_UI(userId) }),
    onError: () =>
      notifications.show({
        color: 'yellow',
        title: 'Sincronizzazione non riuscita',
        message: 'Preferenza salvata solo su questo dispositivo.',
      }),
  });

  /** La sola preferenza che e' cambiata: il resto lo conserva la fusione. */
  const syncRemote = (patch: Partial<UiPrefs>) => {
    if (!userId) return;
    upsert.mutate(patch);
  };

  const persist = (key: string, value: string) => {
    writeKey(key, value);
    notifications.show({ color: 'green', message: 'Preferenza salvata', autoClose: 1500 });
  };

  return (
    <Stack>
      <Group gap={10} align="center">
        <Title order={2}>Preferenze</Title>
        {/* La pagina resta usabile durante la lettura — i valori locali del browser sono
            autoritativi quando ci sono — ma **che una lettura sia in corso va detto**: senza,
            chi cambia un'impostazione in questa finestra sovrascrive un valore remoto che non
            ha ancora visto. Rotella accanto al titolo, non al posto del form. */}
        {prefQ.isLoading && <Loader size="xs" aria-label="Caricamento delle preferenze del tuo account" />}
      </Group>
      {/* Le preferenze salvate sul server non sono arrivate: la pagina funziona con quelle
          locali del browser, ma salvando si sovrascriverebbero le remote. Va detto. */}
      {prefQ.isError && (
        <MaeAdvice
          tone="warning"
          title="Preferenze salvate sul sistema non disponibili"
          selfRemedy="Ricarica la pagina prima di salvare, per non sovrascrivere le impostazioni salvate su un altro dispositivo."
        >
          Stai vedendo le preferenze memorizzate su questo browser. Non è stato possibile
          leggere quelle del tuo account.
        </MaeAdvice>
      )}
      {/* Il selettore del tema resta fuori finche il tema scuro e sospeso: sceglierlo non
          avrebbe effetto, perche il provider forza il chiaro. Vedi `temaScuro.ts`. */}
      {DARK_THEME_ENABLED && (
      <Card withBorder padding="lg" radius="lg">
        <Stack>
          <Group justify="space-between">
            <div>
              <Text fw={600}>Tema</Text>
              <Text size="sm" c="dimmed">Chiaro, scuro o segui sistema</Text>
            </div>
            <SegmentedControl
              /* ⚠️ Qui si accende `scelto`, non `effettivo`: e' la differenza con i comandi
                 rapidi (la tendina del desktop, il foglio del telefono), che accendono cio' che
                 si **vede**. La', «Auto» non e' un'opzione; qui lo e' — e sciogliere `auto` in
                 chiaro o scuro direbbe a chi ha scelto «come il sistema» che non ha scelto
                 niente. Vedi `preferenzaTema.ts`. */
              value={theme.chosen}
              onChange={(v) => theme.choose(v as MantineColorScheme)}
              data={[
                { label: 'Chiaro', value: 'light' },
                { label: 'Scuro', value: 'dark' },
                { label: 'Auto', value: 'auto' },
              ]}
            />
          </Group>
        </Stack>
      </Card>
      )}
      <Card withBorder padding="lg" radius="lg">
        <Stack>
          <Group justify="space-between" wrap="wrap">
            <div>
              <Text fw={600}>Apertura del dettaglio</Text>
              <Text size="sm" c="dimmed">
                Come si apre la scheda di un task quando la selezioni
              </Text>
            </div>
            {/*
              Si accende `chosen` e non `surface`: qui non c'e' la colonna, quindi
              `surface` direbbe «pannello laterale» a chi ha scelto la colonna.

              ⚠️ **Quattro opzioni e non tre.** «Come decide l'azienda» scrive `null`,
              cioe' torna a ereditare. Senza, chi sceglie una volta non puo' piu'
              tornare indietro: e' la stessa ragione per cui il controllo del tenant,
              nella sezione «Branding» di «Dati dell'azienda», ha «Sceglie la persona».
            */}
            <SegmentedControl
              value={surface.decidedBy === 'persona' ? surface.chosen : 'azienda'}
              onChange={(v) => surface.setChosen(v === 'azienda' ? null : (v as DetailSurfaceKind))}
              data={[
                { label: 'Come decide l’azienda', value: 'azienda' },
                ...SURFACE_CHOICES.map((o) => ({ label: o.label, value: o.value })),
              ]}
            />
          </Group>

          {surface.decidedBy !== 'persona' && (
            <MaeAdvice
              tone="info"
              title={surface.decidedBy === 'azienda'
                ? 'Quello che vedi l’ha scelto la tua azienda'
                : 'Nessuno ha ancora scelto'}
              selfRemedy="Scegliendo qui la tua scelta vince, e resta anche se l’azienda cambia idea."
            >
              {surface.decidedBy === 'azienda'
                ? 'La scheda si apre come l’azienda ha impostato: finché non scegli tu, cambia quando lo cambia lei.'
                : 'Vale il predefinito: il pannello laterale, che funziona su ogni pagina e su ogni schermo.'}
            </MaeAdvice>
          )}

          <Text size="xs" c="dimmed">
            La colonna nella pagina richiede una finestra larga e una pagina che la
            preveda: dove non c'e' posto la scheda si apre nel pannello laterale.
          </Text>
        </Stack>
      </Card>
      <Card withBorder padding="lg" radius="lg">
        <Stack>
          <Group justify="space-between" wrap="wrap">
            <div>
              <Text fw={600}>Intestazione delle pagine</Text>
              <Text size="sm" c="dimmed">
                Titolo, descrizione e comandi in cima a ogni pagina, sul computer
              </Text>
            </div>
            <SegmentedControl
              value={pageHeader.visible ? 'visible' : 'hidden'}
              onChange={(v) => pageHeader.setVisible(v === 'visible')}
              data={[
                { label: 'Aperta', value: 'visible' },
                { label: 'Chiusa', value: 'hidden' },
              ]}
            />
          </Group>

          {pageHeader.decidedBy !== 'persona' && (
            <MaeAdvice
              tone="info"
              title={pageHeader.decidedBy === 'azienda'
                ? 'Quello che vedi l’ha scelto la tua azienda'
                : 'Nessuno ha ancora scelto'}
              selfRemedy="Scegliendo qui la tua scelta vince, e resta anche se l’azienda cambia idea."
            >
              {pageHeader.decidedBy === 'azienda'
                ? 'Le pagine si aprono come l’azienda ha impostato: finché non scegli tu, cambia quando lo cambia lei.'
                : 'Vale il predefinito: l’intestazione aperta.'}
            </MaeAdvice>
          )}

          <Text size="xs" c="dimmed">
            È come si apre ogni pagina. La freccia in alto la chiude o la riapre solo
            sulla pagina in cui sei.
          </Text>
        </Stack>
      </Card>
      <Card withBorder padding="lg" radius="lg">
        <Stack>
          <Select
            label="Lingua"
            value={lang}
            onChange={(v) => {
              if (!v) return;
              setLang(v);
              persist(STORAGE_KEYS.lang, v);
              syncRemote({ lang: v });
            }}
            data={LANGUAGES}
          />
          <Select
            label="Fuso orario"
            value={tz}
            onChange={(v) => {
              if (!v) return;
              setTz(v);
              persist(LS_TZ, v);
              syncRemote({ tz: v });
            }}
            data={TIMEZONES}
          />
          <Select
            label="Formato data"
            value={df}
            onChange={(v) => {
              if (!v) return;
              setDf(v);
              persist(LS_DF, v);
              syncRemote({ dateFormat: v });
            }}
            data={DATE_FORMATS}
          />
        </Stack>
      </Card>

      {/*
        LE PAGINE DELLA BARRA DEL TELEFONO — e sì, anche da desktop.
        =============================================================

        ⚠️ La barra si vede solo sul telefono, ma la si compone **anche da qui**, e non è una
        svista: è un riordino con un tetto su un catalogo di centonovanta voci, cioè il lavoro
        che una tastiera fa meglio di un pollice. Nasconderla su schermo largo vorrebbe dire che
        la barra del telefono la si può comporre solo dal telefono.

        Le altre due scelte — forma e ritiro — stanno invece **nel foglio** che si apre dalla
        barra, perché il loro effetto si guarda: da questa pagina, su schermo stretto, si
        coprirebbe proprio la barra di cui si parla.
      */}
      <Card withBorder padding="lg" radius="lg" ref={barDetail} id="barra">
        <Stack gap="sm">
          <div>
            <Text fw={600}>Pagine nella barra del telefono</Text>
            <Text size="sm" c="dimmed">
              Fino a {MAX_ENTRIES} collegamenti, nell&apos;ordine in cui li metti.
              {choices.length === 0 && ' Ora vale la barra predefinita.'}
            </Text>
          </div>

          {choices.length === 0 ? (
            /*
              ⚠️ Descrive lo **stato**, non comanda un gesto — e `pageStateCoverage` lo pretende:
              un vuoto che dice a parole «aggiungi…» indica un comando invece di offrirlo, e la
              via d'uscita di solito è una CTA. Qui no: il campo per aggiungere è la riga
              **subito sotto**, visibile senza cercarla, quindi un bottone qui sarebbe un secondo
              comando per la stessa cosa a due centimetri di distanza.
            */
            <EmptyState
              title="Stai usando la barra predefinita"
              description="Finché non ne componi una tua, la barra mostra i collegamenti di fabbrica — e cambia se cambiano loro."
            />
          ) : (
            <Stack gap={6}>
              {choices.map((to, i) => (
                <Group key={to} justify="space-between" wrap="nowrap"
                  style={{ border: '1px solid var(--mantine-color-default-border)', borderRadius: 8, padding: '6px 8px' }}>
                  <Text size="sm" style={{ minWidth: 0 }}>
                    {/* ⚠️ Il nome dal catalogo, e il percorso quando il catalogo non la conosce:
                        una pagina che oggi non si può vedere resta scelta e va **mostrata**, o
                        sparirebbe dall'elenco senza che nessuno sappia di averla ancora. */}
                    {nameOf(to) ?? to}
                    {!nameOf(to) && (
                      <Text span size="xs" c="dimmed"> — non disponibile ora</Text>
                    )}
                  </Text>
                  <Group gap={4} wrap="nowrap">
                    <ActionIcon variant="subtle" aria-label={`Sposta ${nameOf(to) ?? to} in su`}
                      disabled={i === 0} onClick={() => write(moveEntry(choices, to, -1))}>
                      <IconArrowUp size={16} />
                    </ActionIcon>
                    <ActionIcon variant="subtle" aria-label={`Sposta ${nameOf(to) ?? to} in giù`}
                      disabled={i === choices.length - 1} onClick={() => write(moveEntry(choices, to, 1))}>
                      <IconArrowDown size={16} />
                    </ActionIcon>
                    <ActionIcon variant="subtle" color="red" aria-label={`Togli ${nameOf(to) ?? to}`}
                      onClick={() => write(toggleEntry(choices, to).entries)}>
                      <IconX size={16} />
                    </ActionIcon>
                  </Group>
                </Group>
              ))}
            </Stack>
          )}

          {/* ⚠️ Al tetto il campo si **spegne e dice perché**, invece di accettare il tocco e
              scartare in silenzio la voce più vecchia: sostituire da sé una scelta di qualcuno è
              un dato cambiato che nessuno ha chiesto. */}
          <Select
            label="Aggiungi una pagina"
            placeholder={choices.length >= MAX_ENTRIES ? 'La barra è piena' : 'Cerca fra le pagine…'}
            searchable
            value={null}
            disabled={choices.length >= MAX_ENTRIES}
            description={choices.length >= MAX_ENTRIES
              ? `Hai già ${MAX_ENTRIES} collegamenti: togline uno per farne entrare un altro.`
              : `${choices.length} di ${MAX_ENTRIES}`}
            data={toAdd}
            onChange={(v) => {
              if (!v) return;
              const { entries, outcome } = toggleEntry(choices, v);
              if (outcome === 'piena') return;
              write(entries);
            }}
            nothingFoundMessage="Nessuna pagina con questo nome"
          />

          {choices.length > 0 && (
            <Group justify="flex-end">
              <Button variant="subtle" size="compact-sm" onClick={() => write([])}>
                Torna alla barra predefinita
              </Button>
            </Group>
          )}
        </Stack>
      </Card>
    </Stack>
  );
}
